<!-- 升级订阅弹窗 -->
<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { queryAllPackage, placeOrder } from '~/apis/subscribe'
import { useToastHandler } from '~/composables/useToastHandler'
import MoreActionCheckbox from '~/assets/icons/moreActionCheckbox.png'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
}>()

const { showErrorToast } = useToastHandler()
const subscribingPlanId = ref<number | null>(null)
const plans = ref<any[]>([])
const loadingPlans = ref(false)
const selectedPlanType = ref<'monthly' | 'yearly'>('yearly')

// Computed property for v-model binding
const modalOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    if (!value) {
      handleClose()
    }
  }
})

const monthlyPlan = computed(() => {
  if (!plans.value || !plans.value.length) return null
  const byName = plans.value.find((p: any) => typeof p.name === 'string' && /month/i.test(p.name))
  return byName || plans.value[0]
})

const yearlyPlan = computed(() => {
  if (!plans.value || !plans.value.length) return null
  const byName = plans.value.find((p: any) => typeof p.name === 'string' && /year/i.test(p.name))
  return byName || plans.value[1] || plans.value[0]
})

const selectedPlan = computed(() => {
  return selectedPlanType.value === 'yearly' ? yearlyPlan.value : monthlyPlan.value
})

// 处理订阅（基于简单的套餐参数，仅使用 gogoodId）
const handleSubscribe = async (plan: { gogoodId?: number | null }) => {
  if (!plan.gogoodId || subscribingPlanId.value === plan.gogoodId) {
    return
  }

  subscribingPlanId.value = plan.gogoodId

  try {
    const url = new URL(window.location.href)
    url.searchParams.set('subscription_purchase_success', '1')
    const successUrl = url.toString()

    const response = await placeOrder({ goodsId: plan.gogoodId, success_url: successUrl })

    if (!response.success || !response.data?.url) {
      throw new Error(response.message || 'Failed to create order')
    }

    // 跳转到 Stripe 支付页面
    if (process.client && response.data.url) {
      window.location.href = response.data.url
    }
  } catch (error: any) {
    showErrorToast(error?.message || 'Failed to process subscription, please try again')
  } finally {
    subscribingPlanId.value = null
  }
}

onMounted(async () => {
  loadingPlans.value = true
  try {
    const res = await queryAllPackage({ showSuccess: false, loading: false })
    const data: any = (res as any)?.data
    if (Array.isArray(data)) {
      plans.value = data
    } else if (data) {
      plans.value = [data]
    }
  } catch (error) {
    // 静默失败，使用默认价格
  } finally {
    loadingPlans.value = false
  }
})

// Handle close from the X button
const handleClose = () => {
  emit('close')
  emit('update:modelValue', false)
}

const features = [
  '3,500 Monthly Credits (No rollover, Non-transferable)',
  'Access to all generation models',
  'Commercial rights for newly generated songs',
  'MP3 downloads',
  'WAV downloads',
  'Extend your own generated music',
  'Cover your own generated music',
  'Generate similar music',
  'Advanced Parameter Controls (Weirdness, Style Influence, Audio Influence)',
  'Fast-track queue: Generate up to 12 songs in one go.'
]
</script>

<template>
  <UModal
    v-model:open="modalOpen"
    :ui="{
      overlay: 'bg-[#101012B2] backdrop-blur-[3px] !z-[9998]',
      content: 'w-[calc(100%-32px)] md:w-[544px] bg-[#2D2D39] rounded-[16px] ring-[none] !z-[9999]'
    }"
    :dismissible="false"
  >
    <template #content>
      <!-- 内层背景内容 -->
      <div class="relative flex flex-col w-full h-full font-['Roboto'] py-[32px] px-[24px] overflow-hidden">
        <!-- 关闭按钮 -->
        <div
          class="absolute top-[10px] right-[10px] flex items-center justify-center w-[34px] h-[34px] cursor-pointer before:content-[''] before:absolute before:w-[20px] before:h-[2px] before:bg-[#8888A1] before:rotate-45 before:rounded-[20px] after:content-[''] after:absolute after:w-[20px] after:h-[2px] after:bg-[#8888A1] after:-rotate-45 after:rounded-[20px]"
          @click="handleClose"
        />

        <!-- 标题 -->
        <h2 class="font-['Vidaloka'] text-[40px] text-white leading-[50px] mb-[40px]">
          Upgrade to Pro
        </h2>

        <div class="overflow-y-auto">
          <div class="flex flex-col gap-y-[16px] mb-[24px]">
            <div
              v-for="featureItem in features"
              :key="featureItem"
              class="flex items-start gap-x-[16px]"
            >
              <img
                class="size-[16px] mt-[2px] rounded-full"
                :src="MoreActionCheckbox"
                alt=""
              >
              <span class="text-[16px] text-[#E7E7EC] leading-[20px]">{{ featureItem }}</span>
            </div>
          </div>

          <div class="flex flex-col gap-y-[16px] mb-[24px]">
            <div
              class="flex items-center justify-between text-[16px] leading-[20px] px-[16px] py-[14px] rounded-[8px] border cursor-pointer"
              :class="selectedPlanType === 'monthly' ? 'text-white border-[#FF383C]' : 'text-[#8888A1] border-[#8888A1]'"
              @click="selectedPlanType = 'monthly'"
            >
              <p>
                Monthly Plan
              </p>
              <p>
                {{ monthlyPlan && monthlyPlan.price ? `$${monthlyPlan.price / 1}/Month` : '$20/Month' }}
              </p>
            </div>

            <div
              class="flex items-center justify-between text-[16px] leading-[20px] px-[16px] py-[14px] rounded-[8px] border cursor-pointer"
              :class="selectedPlanType === 'yearly' ? 'text-white border-[#FF383C]' : 'text-[#8888A1] border-[#8888A1]'"
              @click="selectedPlanType = 'yearly'"
            >
              <p class="font-medium">
                Yearly Plan
              </p>
              <p class="font-medium">
                {{ yearlyPlan && yearlyPlan.price ? `$${yearlyPlan.price / 12}/Month` : '$10/Month' }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-y-[24px]">
          <div
            class="flex justify-center items-center text-[20px] text-white leading-[24px] bg-[#9400D4] rounded-[8px] py-[12px] cursor-pointer"
            @click="handleSubscribe({ gogoodId: selectedPlan?.id || yearlyPlan?.id || monthlyPlan?.id || 71 })"
          >
            Subscribe
          </div>
          <div
            class="text-[16px] text-[#8888A1] text-center cursor-pointer"
            @click="navigateTo('/subscribe');handleClose()"
          >
            More Plan >
          </div>
        </div>
      </div>
    </template>
  </UModal>
</template>
